import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from "chart.js";
import { Bar, Pie, Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

export default function AnalyticsDashboard() {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetch("https://lead-tracker-backend-gyoa.onrender.com/api/analytics")
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching data:", err));
  }, []);

  // Filter data
  const filteredData =
    filter === "all"
      ? data
      : data.filter((item) => item.pageUrl.includes(filter));

  // KPI Numbers
  const totalClients = new Set(data.map((d) => d.clientId)).size;
  const totalVisits = data.length;
  const uniqueIPs = new Set(data.map((d) => d.ipAddress)).size;

  // Chart Data
  const visitsPerClient = {};
  filteredData.forEach((d) => {
    visitsPerClient[d.clientId] = (visitsPerClient[d.clientId] || 0) + 1;
  });

  const barData = {
    labels: Object.keys(visitsPerClient),
    datasets: [
      {
        label: "Visits per Client",
        data: Object.values(visitsPerClient),
        backgroundColor: "#1976d2",
      },
    ],
  };

  const ipDistribution = {};
  filteredData.forEach((d) => {
    ipDistribution[d.ipAddress] = (ipDistribution[d.ipAddress] || 0) + 1;
  });

  const pieData = {
    labels: Object.keys(ipDistribution),
    datasets: [
      {
        label: "IP Distribution",
        data: Object.values(ipDistribution),
        backgroundColor: ["#42a5f5", "#66bb6a", "#ffa726", "#ef5350", "#ab47bc"],
      },
    ],
  };

  const lineData = {
    labels: filteredData.map((d) =>
      new Date(d.timestamp).toLocaleDateString()
    ),
    datasets: [
      {
        label: "Visits Over Time",
        data: filteredData.map((d) => 1),
        borderColor: "#1976d2",
        backgroundColor: "rgba(25, 118, 210, 0.3)",
        fill: true,
      },
    ],
  };

  return (
    <Box sx={{ p: 4, backgroundColor: "#f9fafc", minHeight: "100vh" }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        📊 Analytics Dashboard
      </Typography>

      {/* KPIs */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { label: "Total Clients", value: totalClients },
          { label: "Total Visits", value: totalVisits },
          { label: "Unique IPs", value: uniqueIPs },
        ].map((kpi, i) => (
          <Grid item xs={12} sm={4} key={i}>
            <Card sx={{ textAlign: "center", p: 2, boxShadow: 3, borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary">
                  {kpi.label}
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {kpi.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Page</InputLabel>
          <Select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <MenuItem value="all">All Pages</MenuItem>
            {Array.from(new Set(data.map((d) => d.pageUrl))).map((url, i) => (
              <MenuItem key={i} value={url}>
                {url}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 3 }}>
            <Typography variant="h6" mb={2}>
              Visits per Client
            </Typography>
            <Bar data={barData} />
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 3 }}>
            <Typography variant="h6" mb={2}>
              IP Distribution
            </Typography>
            <Pie data={pieData} />
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 3 }}>
            <Typography variant="h6" mb={2}>
              Visits Over Time
            </Typography>
            <Line data={lineData} />
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
